/**
 * Mémoire locale de la campagne — pour ne **jamais** perdre une saisie.
 *
 * La table de correspondance N-1 → N (CAM-03) n’est pas stockée côté serveur :
 * `POST /campagne/:saison/correspondances` ne fait que chiffrer un aperçu, et
 * les correspondances ne sont transmises qu’au moment de générer. Or c’est un
 * travail humain qui peut prendre une demi-heure et plusieurs allers-retours
 * avec l’écran des formules. On la conserve donc dans le navigateur, par couple
 * de saisons, jusqu’à la génération.
 *
 * Rien de confidentiel n’y transite : uniquement des identifiants de formules.
 */

import type { CorrespondanceFormule } from '@tb/shared'

const PREFIXE = 'tb.campagne'

/** `localStorage` peut être indisponible (mode privé, quota) : jamais bloquant. */
function lire(cle: string): string | null {
  try {
    return globalThis.localStorage?.getItem(cle) ?? null
  } catch {
    return null
  }
}

function ecrire(cle: string, valeur: string): void {
  try {
    globalThis.localStorage?.setItem(cle, valeur)
  } catch {
    /* Le confort de persistance n’est jamais une raison d’échouer. */
  }
}

function effacer(cle: string): void {
  try {
    globalThis.localStorage?.removeItem(cle)
  } catch {
    /* idem */
  }
}

/* -------------------------------------------------------------------------- */
/* Saison de travail                                                          */
/* -------------------------------------------------------------------------- */

const CLE_SAISON = `${PREFIXE}.saison`

/** La saison sur laquelle le poste travaillait la dernière fois. */
export function lireDerniereSaison(): string | null {
  return lire(CLE_SAISON)
}

export function memoriserSaison(saisonId: string): void {
  ecrire(CLE_SAISON, saisonId)
}

/** La saison N-1 choisie pour reconduire les abonnés de la saison N. */
export function lireSaisonN1(saisonId: string): string | null {
  return lire(`${PREFIXE}.n1.${saisonId}`)
}

export function memoriserSaisonN1(saisonId: string, saisonN1Id: string): void {
  ecrire(`${PREFIXE}.n1.${saisonId}`, saisonN1Id)
}

/* -------------------------------------------------------------------------- */
/* Table de correspondance                                                    */
/* -------------------------------------------------------------------------- */

function cleCorrespondances(saisonId: string, saisonN1Id: string): string {
  return `${PREFIXE}.correspondances.${saisonId}.${saisonN1Id}`
}

/** Relit la table de correspondance saisie précédemment. Jamais d’exception. */
export function lireCorrespondances(saisonId: string, saisonN1Id: string): CorrespondanceFormule[] {
  const brut = lire(cleCorrespondances(saisonId, saisonN1Id))
  if (brut === null) return []
  try {
    const valeur: unknown = JSON.parse(brut)
    if (!Array.isArray(valeur)) return []
    return valeur.filter(estCorrespondance)
  } catch {
    return []
  }
}

export function memoriserCorrespondances(
  saisonId: string,
  saisonN1Id: string,
  correspondances: readonly CorrespondanceFormule[],
): void {
  ecrire(cleCorrespondances(saisonId, saisonN1Id), JSON.stringify(correspondances))
}

/** Après une génération réussie, la saisie a rempli son office. */
export function oublierCorrespondances(saisonId: string, saisonN1Id: string): void {
  effacer(cleCorrespondances(saisonId, saisonN1Id))
}

function estCorrespondance(valeur: unknown): valeur is CorrespondanceFormule {
  if (typeof valeur !== 'object' || valeur === null) return false
  const { formuleN1Id, formuleNId } = valeur as Record<string, unknown>
  return typeof formuleN1Id === 'string' && (typeof formuleNId === 'string' || formuleNId === null)
}
